import React from 'react';
import { metadataMeta } from '~/lib/utils/meta-utils-client';
import { metadataLoader } from '~/lib/utils/meta-utils-server';

export const loader = metadataLoader((t) => ({
  title: t('common.routes.termsConditions.title'),
  description: t('common.routes.termsConditions.description'),
}));

export const meta = metadataMeta<typeof loader>();

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-6">
    <h2 className="text-2xl font-bold">{title}</h2>
    <div className="space-y-2 opacity-70">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <main className="max-w-prose space-y-12 p-8 pt-16">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">ScoreOrNot Terms and Conditions</h1>
        <p className="text-sm opacity-50">Last Updated: March 2026</p>
      </header>

      <p>
        Operated by Vivaplay d.o.o., Primorska ulica 13, 1000 Ljubljana,
        Slovenia.
      </p>

      <Section title="1. About ScoreOrNot">
        <p>
          ScoreOrNot is a skill-based sports prediction platform operated by
          Vivaplay d.o.o. Users predict outcomes of sports video clips in a
          gamified, competitive environment. The platform does not involve
          betting or gambling.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <ul>
          <li>ScoreOrNot is available to users aged 18 and over worldwide.</li>
          <li>
            You must provide accurate information when creating an account.
          </li>
          <li>
            You are responsible for the security of your account credentials.
          </li>
          <li>
            Multiple accounts from the same device or IP address are not
            permitted.
          </li>
        </ul>
      </Section>

      <Section title="3. Platform Usage">
        <ul>
          <li>
            Use ScoreOrNot for legitimate sports prediction activities only.
          </li>
          <li>
            Respect other users and maintain a positive community environment.
          </li>
          <li>Using automated systems, bots, or scripts.</li>
          <li>
            Do not attempt to hack, disrupt, or reverse-engineer the platform.
          </li>
          <li>Do not share inappropriate, offensive, or unlawful content.</li>
        </ul>
      </Section>

      <Section title="4. Privacy and Consent">
        <p>
          Your personal data is processed in accordance with our Privacy Policy,
          which forms part of these Terms.
        </p>

        <div className="space-y-2">
          <p className="font-semibold opacity-100">
            Checkbox A - required at registration:
          </p>
          <p>
            [ ] I have read and agree to the ScoreOrNot Terms and Conditions and
            Privacy Policy. I confirm that I am 18 years of age or older.
          </p>
          <p>
            This checkbox is mandatory. You cannot create an account without
            ticking it.
          </p>
        </div>

        <div className="space-y-2">
          <p className="font-semibold opacity-100">
            Checkbox B - optional, shown only at partner club events:
          </p>
          <p>
            [ ] I agree to share my contact data with [Club Name] so they can
            send me information about their events, offers, and partner
            promotions. I understand this is optional and I can withdraw my
            consent at any time in Profile &gt; Privacy &amp; Consents.
          </p>
          <p>
            This checkbox is unchecked by default, voluntary, and has no effect
            on your access to ScoreOrNot.
          </p>
        </div>
      </Section>

      <Section title="5. Partner Club Events and Tickets">
        <p>
          Partner clubs may make tickets available to ScoreOrNot users who have
          opted in to share their data with that club. Ticket availability is
          determined by the partner club and subject to availability. ScoreOrNot
          distributes tickets on behalf of partner clubs and does not guarantee
          availability. Details are communicated per event.
        </p>
      </Section>

      <Section title="6. Intellectual Property">
        <ul>
          <li>
            All ScoreOrNot content (videos, graphics, software) is owned by
            ScoreOrNot or its content partners.
          </li>
          <li>
            Users may not reproduce, distribute, or create derivative works from
            platform content.
          </li>
        </ul>
      </Section>

      <Section title="7. Availability">
        <p>
          ScoreOrNot is provided &quot;as-is&quot;. We do not guarantee
          uninterrupted service and are not liable for interruptions or
          device-specific issues.
        </p>
      </Section>

      <Section title="8. Account Termination">
        <p>
          We may suspend or terminate accounts that violate these Terms. Either
          party may terminate at any time; upon termination, access to
          ScoreOrNot ceases immediately.
        </p>
      </Section>

      <Section title="9. Liability">
        <p>
          ScoreOrNot is provided for entertainment purposes. We are not liable
          for indirect, incidental, or consequential damages. Our total
          liability is limited to the amount paid by the user, if any.
        </p>
      </Section>

      <Section title="10. Governing Law">
        <p>
          These Terms are governed by the laws of Slovenia. Disputes should
          first be addressed via customer support at{' '}
          <a href="mailto:contact@scoreornot.com">contact@scoreornot.com</a>.{' '}
          The competent court is in Ljubljana.
        </p>
      </Section>

      <Section title="11. Changes">
        <p>
          Material changes communicated at least 14 days before taking effect.
          Continued use constitutes acceptance.
        </p>
      </Section>

      <Section title="12. Contact">
        <p>
          <a href="mailto:contact@scoreornot.com">contact@scoreornot.com</a>
          {' | '}
          <a href="mailto:privacy@scoreornot.com">privacy@scoreornot.com</a>
          {' | '}
          Vivaplay d.o.o., Primorska ulica 13, 1000 Ljubljana, Slovenia.
        </p>
        <p>
          <a
            href="https://scoreornot.com"
            target="_blank"
            rel="noopener noreferrer">
            scoreornot.com
          </a>
        </p>
      </Section>

      <footer>
        <p>© 2026 Vivaplay d.o.o. | scoreornot.com | privacy@scoreornot.com</p>
      </footer>
    </main>
  );
}
